import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { ApiError } from "../../../lib/http/errors";
import { Button } from "../../../components/ui/Button";
import { ErrorBanner } from "../../../components/Shell";
import { hasMessage, useT } from "../../../i18n";
import { Conflict, flattenConfig, unflattenConfig, ValidationError } from "../api/opsApi";
import type { OpsApi } from "../api/opsApi";
import type { ConfigVersion, Issue, Json } from "../types";
import {
  checkValue,
  choicesOf,
  impactKey,
  metaOf,
  rangeOf,
  RUNTIME_KEYS,
} from "../lib/runtimeKeys";

/**
 * FS-15-04 런타임 설정 — SCR v0.6 sfs-15-04 · SR-CAT-03.
 *  - 설정 키 표: 키 · 현재(편집) · 기본 · 범위 · 영향 — 바뀐 행은 노랑(상태 '변경됨')
 *  - 변경 이력(버전 · 날짜 · 누가 · 바뀐 키 이전→이후) · 적용 범위 안내('진행 중 룸은 현재 버전 유지 · 신규 룸부터')
 *  - 변경 취소 · 저장 · v{n+1}(POST {config, baseVersion}) — 409 = 다른 운영자가 먼저 바꿈(새로 불러오기) · 422 = 행 표시
 *  - P3-S6: `always` 키(관찰자 공유 3키)는 저장된 설정에 없으면 기본값으로 보이고 '기본값 · 저장 안 됨' 표시 —
 *    그 값을 바꿔 저장하면 설정에 들어간다. bool · enum 키는 목록에서 고른다.
 */
export const fmtDate = (iso: string | null) => (iso ? iso.slice(0, 10) : "—");
const show = (v: Json | undefined) =>
  v === undefined ? "∅" : typeof v === "string" ? v : JSON.stringify(v);

/** 입력 문자열 → 원래 값 모양(숫자면 숫자, 그 밖은 JSON 으로 읽어 보고 안 되면 문자열) */
export function parseValue(raw: string, was: Json | undefined): Json {
  const s = raw.trim();
  if (typeof was === "number" && /^-?\d+(\.\d+)?$/.test(s)) return Number(s);
  if (typeof was === "string") return raw;
  try {
    return JSON.parse(s) as Json;
  } catch {
    return raw;
  }
}

/** 서버 오류 경로('/tick/substeps' · 'tick.substeps') → 점 표기 */
export const normPath = (p: string) => p.replace(/^\//, "").replace(/\//g, ".");

export function ConfigTab({
  api,
  onVersion,
}: {
  api: OpsApi;
  onVersion: (v: number | null) => void;
}) {
  const t = useT();
  const id = useId();
  const [cur, setCur] = useState<ConfigVersion | null>(null);
  const [history, setHistory] = useState<ConfigVersion[]>([]);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [issues, setIssues] = useState<Issue[]>([]);
  const [conflict, setConflict] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, h] = await Promise.all([api.runtimeConfig(), api.runtimeHistory()]);
      setCur(c);
      setHistory(h);
      setDraft({});
      setIssues([]);
      setConflict(false);
      onVersion(c?.version ?? null);
    } catch (e) {
      setFailure(e instanceof ApiError ? e.code : "NET-SERVER");
    }
  }, [api, onVersion]);

  useEffect(() => {
    void load();
  }, [load]);

  const flat = useMemo(() => (cur ? flattenConfig(cur.config) : {}), [cur]);
  /** 저장된 값 — 없으면(always 키) 기본값 */
  const base = (k: string): Json | undefined => (k in flat ? flat[k] : metaOf(k)?.def);
  const keys = useMemo(() => {
    const known = RUNTIME_KEYS.filter((m) => m.key in flat || m.always).map((m) => m.key);
    return [
      ...known,
      ...Object.keys(flat)
        .filter((k) => !known.includes(k))
        .sort(),
    ];
  }, [flat]);

  const changed = keys.filter((k) => k in draft && draft[k] !== show(base(k)));
  const localErr = (k: string) => (k in draft ? checkValue(k, draft[k]!) : null);
  const serverErr = (k: string) => issues.find((i) => normPath(i.path) === k)?.message ?? null;
  const blocked = changed.some((k) => localErr(k));

  async function save() {
    if (!cur || blocked || changed.length === 0) return;
    const next: Record<string, Json> = { ...flat };
    for (const k of changed) next[k] = parseValue(draft[k]!, base(k));
    setSaving(true);
    setFailure(null);
    setIssues([]);
    try {
      await api.saveRuntimeConfig(unflattenConfig(next), cur.version);
      await load();
    } catch (e) {
      if (e instanceof ValidationError) setIssues(e.issues);
      else if (e instanceof Conflict) setConflict(true);
      else setFailure(e instanceof ApiError ? e.code : "NET-SERVER");
    } finally {
      setSaving(false);
    }
  }

  if (!cur)
    return failure ? (
      <ErrorBanner
        cause={t("ops.common.loadFailed")}
        action={t("ops.common.retry")}
        code={failure}
      />
    ) : (
      <p className="pf-muted">{t("common.loading")}</p>
    );

  return (
    <div className="pf-ops-config" data-testid="ops-config">
      <section className="pf-pane" aria-labelledby={`${id}-h`}>
        <h2 id={`${id}-h`} className="pf-h3">
          {t("ops.config.title")}
        </h2>
        <div>
          <table className="pf-tbl" data-testid="config-table">
            <thead>
              <tr>
                <th scope="col">{t("ops.config.colKey")}</th>
                <th scope="col">{t("ops.config.colValue")}</th>
                <th scope="col">{t("ops.config.colDefault")}</th>
                <th scope="col">{t("ops.config.colRange")}</th>
                <th scope="col">{t("ops.config.colImpact")}</th>
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => {
                const m = metaOf(k);
                const value = k in draft ? draft[k]! : show(base(k));
                const choices = choicesOf(m);
                const unsaved = !(k in flat);
                const err = localErr(k)
                  ? t(`ops.config.err.${localErr(k)}`, {
                      min: m?.min ?? "",
                      max: m?.max ?? "",
                      list: choices?.join(" | ") ?? "",
                    })
                  : serverErr(k);
                const range = m ? rangeOf(m) : null;
                const onEdit = (v: string) => {
                  setDraft((d) => ({ ...d, [k]: v }));
                  setIssues((xs) => xs.filter((i) => normPath(i.path) !== k));
                };
                const isChanged = changed.includes(k);
                return (
                  <tr
                    key={k}
                    data-key={k}
                    data-unsaved={unsaved || undefined}
                    className={
                      err ? "pf-tbl__row--warn" : isChanged ? "pf-tbl__row--changed" : undefined
                    }
                  >
                    <th scope="row">
                      <label htmlFor={`${id}-${k}`}>{k}</label>
                      {unsaved && (
                        <span
                          className="pf-chip-static pf-ops-config__unsaved"
                          data-testid="config-unsaved"
                        >
                          {t("ops.config.unsaved")}
                        </span>
                      )}
                    </th>
                    <td>
                      {choices ? (
                        <select
                          id={`${id}-${k}`}
                          className="pf-input"
                          value={value}
                          disabled={m?.fixed}
                          aria-invalid={err ? true : undefined}
                          aria-describedby={err ? `${id}-${k}-err` : undefined}
                          data-testid="config-input"
                          onChange={(e) => onEdit(e.target.value)}
                        >
                          {!choices.includes(value) && <option value={value}>{value}</option>}
                          {choices.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          id={`${id}-${k}`}
                          className={`pf-input${typeof flat[k] === "number" ? " pf-input--num" : ""}`}
                          type="text"
                          inputMode={typeof flat[k] === "number" ? "numeric" : undefined}
                          value={value}
                          readOnly={m?.fixed}
                          aria-invalid={err ? true : undefined}
                          aria-describedby={err ? `${id}-${k}-err` : undefined}
                          data-testid="config-input"
                          onChange={(e) => onEdit(e.target.value)}
                        />
                      )}
                      {err && (
                        <span id={`${id}-${k}-err`} className="pf-field-err" role="alert">
                          {" "}
                          {err}
                        </span>
                      )}
                    </td>
                    <td className="pf-muted">{m ? String(m.def) : "—"}</td>
                    <td className="pf-muted">
                      {!range
                        ? "—"
                        : "fixed" in range
                          ? t("ops.config.fixed")
                          : "list" in range
                            ? range.list
                            : `${range.min}~${range.max}`}
                    </td>
                    <td className="pf-muted">{hasMessage(impactKey(k)) ? t(impactKey(k)) : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <div className="pf-settings__top">
        <section className="pf-pane" aria-labelledby={`${id}-hist`} data-testid="config-history">
          <h2 id={`${id}-hist`} className="pf-h3">
            {t("ops.config.history")}
          </h2>
          <ol className="pf-note">
            {history.map((h, i) => {
              const prev = history[i + 1] ? flattenConfig(history[i + 1]!.config) : {};
              const now = flattenConfig(h.config);
              return (
                <li key={h.version} data-version={h.version}>
                  <b>v{h.version}</b> · {fmtDate(h.createdAt)} · {h.changedBy ?? "—"} ·{" "}
                  {h.changedKeys.length
                    ? h.changedKeys
                        .map((k) => (k in prev ? `${k} ${show(prev[k])}→${show(now[k])}` : k))
                        .join(", ")
                    : "—"}
                </li>
              );
            })}
          </ol>
        </section>
        <section className="pf-pane" aria-labelledby={`${id}-apply`}>
          <h2 id={`${id}-apply`} className="pf-h3">
            {t("ops.config.apply")}
          </h2>
          <div className="pf-banner pf-banner--warn" role="note" data-testid="scope-note">
            <p className="pf-banner__text">{t("ops.config.scope")}</p>
          </div>
          {conflict && (
            <div className="pf-banner pf-banner--err" role="alert" data-testid="config-conflict">
              <div className="pf-banner__body">
                <p className="pf-banner__text">
                  {t("ops.config.conflict")} <code className="pf-banner__code">(HTTP-409)</code>
                </p>
                <div className="pf-banner__actions">
                  <Button variant="ghost" className="pf-btn--sm" onClick={() => void load()}>
                    {t("ops.config.reload")}
                  </Button>
                </div>
              </div>
            </div>
          )}
          {issues.length > 0 && (
            <ErrorBanner
              cause={t("ops.config.invalid", {
                list: issues.map((i) => `${i.path}: ${i.message}`).join(" · "),
              })}
              action={t("ops.config.invalidNext")}
              code="HTTP-422"
            />
          )}
          {failure && (
            <ErrorBanner
              cause={t("ops.common.saveFailed")}
              action={t("ops.common.retry")}
              code={failure}
            />
          )}
          <div className="pf-row">
            <Button
              variant="ghost"
              className="pf-btn--sm"
              disabled={changed.length === 0 || saving}
              data-testid="config-cancel"
              onClick={() => {
                setDraft({});
                setIssues([]);
              }}
            >
              {t("ops.config.cancel")}
            </Button>
            <Button
              variant="accent"
              className="pf-btn--sm"
              disabled={changed.length === 0 || blocked || saving}
              data-testid="config-save"
              onClick={() => void save()}
            >
              {t("ops.config.save", { v: cur.version + 1 })}
            </Button>
          </div>
          <span className="pf-sr-only" role="status">
            {changed.length ? t("ops.config.changedCount", { n: changed.length }) : ""}
          </span>
        </section>
      </div>
    </div>
  );
}
